import { useEffect, useState } from 'react'

const THEME_CHANGE_EVENT = 'centrali-theme-change'

/**
 * Single source of truth for app-wide theme management.
 * All instances stay in sync via a custom broadcast event so that changing
 * the theme in UserCard is immediately reflected in the layout and all other consumers.
 *
 * Usage in any component:
 *   const { isDarkTheme, theme, setTheme } = useThemeManager()
 */
export function useThemeManager() {
  const [theme, setThemeState] = useState<string>(
    () => localStorage.getItem('centrali-theme') || 'system'
  )
  const [isDarkTheme, setIsDarkTheme] = useState(false)

  // Apply theme whenever `theme` changes
  useEffect(() => {
    const root = document.documentElement
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const applyTheme = (selectedTheme: string) => {
      const darkMode =
        selectedTheme === 'dark' || (selectedTheme === 'system' && mediaQuery.matches)

      if (darkMode) {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }

      setIsDarkTheme(darkMode)
    }

    applyTheme(theme)
    localStorage.setItem('centrali-theme', theme)

    const handleSystemChange = () => {
      if (theme === 'system') applyTheme('system')
    }

    mediaQuery.addEventListener('change', handleSystemChange)
    return () => mediaQuery.removeEventListener('change', handleSystemChange)
  }, [theme])

  // Listen for theme changes broadcast from other hook instances
  useEffect(() => {
    const handleExternalChange = (e: Event) => {
      const newTheme = (e as CustomEvent<string>).detail
      if (newTheme && newTheme !== theme) {
        setThemeState(newTheme)
      }
    }
    window.addEventListener(THEME_CHANGE_EVENT, handleExternalChange)
    return () => window.removeEventListener(THEME_CHANGE_EVENT, handleExternalChange)
  }, [theme])

  // Public setter: updates local state AND broadcasts to all other consumers
  const setTheme = (newTheme: string) => {
    setThemeState(newTheme)
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: newTheme }))
  }

  return { isDarkTheme, theme, setTheme }
}
